import React, { useState, useEffect } from 'react';
import { useColorContext } from '../../../context/colorContext';
import MathContentLayer from '../../ui/math/MathContentLayer';

const SmartFlashCardElement = ({ show = '', hide = '', style = {}, className = '', onFlip, ...props }) => {
  const [flipped, setFlipped] = useState(false);
  const { bgSecondary, bgTertiary, textPrimary, textSecondary, activeAccent } = useColorContext();

  useEffect(() => {
    setFlipped(false);
  }, [show, hide]);

  const toggleFlip = (event) => {
    if (event?.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event?.preventDefault?.();
    const nextState = !flipped;
    setFlipped(nextState);
    onFlip?.(nextState);
  };

  const containerStyle = {
    perspective: '1200px',
    perspectiveOrigin: '50% 50%',
    width: '100%',
    maxWidth: '28rem',
    margin: '0 auto',
    ...style,
  };

  const cardStyle = {
    position: 'relative',
    width: '100%',
    minHeight: '220px',
    borderRadius: '1.85rem',
    transformStyle: 'preserve-3d',
    transformOrigin: '50% 50%',
    transition: 'transform 0.62s cubic-bezier(0.23, 1, 0.32, 1), box-shadow 0.62s ease',
    transform: flipped ? 'rotate3d(1, 0, 0, 180deg)' : 'rotate3d(1, 0, 0, 0deg)',
    boxShadow: `0 24px 60px rgba(0, 0, 0, 0.24), 0 0 0 1px rgba(255,255,255,0.03)`,
    cursor: 'pointer',
    willChange: 'transform',
  };

  const faceBaseStyle = {
    position: 'absolute',
    inset: 0,
    borderRadius: '1.85rem',
    padding: '1.8rem',
    backfaceVisibility: 'hidden',
    WebkitBackfaceVisibility: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '1rem',
    color: textPrimary,
    border: `1px solid ${activeAccent}20`,
    backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0))`,
    backdropFilter: 'blur(16px)',
  };

  const frontStyle = {
    ...faceBaseStyle,
    backgroundColor: `${bgSecondary}`,
  };

  const backStyle = {
    ...faceBaseStyle,
    backgroundColor: `${bgTertiary}`,
    transform: 'rotate3d(1, 0, 0, 180deg)',
  };

  const topAccentStyle = {
    width: '4rem',
    height: '0.35rem',
    borderRadius: '999px',
    background: `linear-gradient(90deg, ${activeAccent}, rgba(255,255,255,0.24))`,
    boxShadow: `0 0 18px ${activeAccent}50`,
    marginBottom: '1rem',
  };

  const headerStyle = {
    fontSize: '0.95rem',
    fontWeight: 700,
    letterSpacing: '0.03em',
    margin: 0,
    color: textPrimary,
    textTransform: 'uppercase',
  };

  const textStyle = {
    fontSize: '1.1rem',
    lineHeight: 1.8,
    color: textPrimary,
    textAlign: 'center',
    width: '100%',
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: '0.5rem',
  };

  const footerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.75rem',
    marginTop: 'auto',
  };

  const labelStyle = {
    padding: '0.45rem 0.85rem',
    borderRadius: '999px',
    fontSize: '0.78rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    color: textPrimary,
    backgroundColor: `${activeAccent}20`,
    alignSelf: 'flex-start',
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={flipped}
      onClick={toggleFlip}
      onKeyDown={toggleFlip}
      className={className}
      style={containerStyle}
      {...props}
    >
      <div style={cardStyle}>
        <div style={frontStyle}>
          <div style={topAccentStyle} />
          <div style={textStyle}>
            <MathContentLayer
              content={show}
              style={{
                width: '100%',
                textAlign: 'center',
                whiteSpace: 'pre-wrap',
              }}
            />
          </div>
          <div style={footerStyle}>
            <span style={{ color: textSecondary, fontSize: '0.82rem' }}>Tap pour retourner</span>
          </div>
        </div>

        <div style={backStyle}>
          <div style={topAccentStyle} />
          <div style={textStyle}>
            <MathContentLayer
              content={hide}
              style={{
                width: '100%',
                textAlign: 'center',
                whiteSpace: 'pre-wrap',
              }}
            />
          </div>
          <div style={footerStyle}>
            <span style={{ color: textSecondary, fontSize: '0.82rem' }}>Tap pour revenir</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartFlashCardElement;
